import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { t } from "i18next";
import { format, parse } from "date-fns";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export function getListLanguages() {
    return [
        {
            key: "vi",
            text: t("vietnamese"),
        },
        {
            key: "en",
            text: t("english"),
        },
    ];
}

// Navigate
let navigate;

export const setNavigate = (nav) => {
    navigate = nav;
};

export const navigateTo = (path, params) => {
    if (navigate) {
        navigate(path, params);
    }
};

// Loading bar
let startLoading;
let completeLoading;

export const setStartLoading = (loading) => {
    startLoading = loading;
};

export const setCompleteLoading = (loading) => {
    completeLoading = loading;
};

export const handleLoading = async (callback) => {
    let res = null;
    try {
        if (startLoading) {
            startLoading();
        }
        res = await callback();
    } catch (error) {
        res = error.response;
    } finally {
        if (completeLoading) {
            completeLoading();
        }
    }
    return res;
};

export function debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
}

export const slugify = (text) => {
    if (!text) return "";

    return (
        text
            .toString()
            .toLowerCase()
            .trim()
            // Thay thế ký tự có dấu tiếng Việt
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            // Thay thế các ký tự đặc biệt tiếng Việt
            .replace(/đ/g, "d")
            .replace(/Đ/g, "D")
            // Thay thế khoảng trắng và ký tự đặc biệt bằng dấu gạch ngang
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            // Xóa dấu gạch ngang ở đầu và cuối
            .replace(/^-+|-+$/g, "")
    );
};

export const getTimeAgoText = (startTime, endTime, typeTime) => {
    let time = "";
    if (startTime < 0 && endTime < 0) {
        time = `${startTime * -1} - ${endTime * -1} ${t("bc")}`;
    }

    if (startTime > 0 && endTime > 0) {
        time = `${startTime}${
            startTime < 1000 ? ` ${t("bce")}` : ""
        } - ${endTime}${endTime < 1000 ? ` ${t("bce")}` : ""}`;
    }

    if (startTime < 0 && endTime > 0) {
        time = `${startTime} ${t("bc")} - ${endTime}${
            endTime < 1000 ? ` ${t("bce")}` : ""
        }`;
    }

    if (endTime == 0) {
        if (startTime < 0) {
            time = `${startTime * -1} ${t("bc")}`;
        } else {
            time = `${typeTime == 2 ? "Những năm " : ""}${startTime}${
                startTime < 1000 ? ` ${t("bce")}` : ""
            }`;
        }
    }

    return time;
};

let cookie = "";
export function setCookieFromServer(val) {
    cookie = val;
}

export function getCookieFromServer() {
    return cookie;
}

let router = null;
export function setRouter(item) {
    router = item;
}

export function getRouter() {
    return router;
}

export function formatCurrencyVND(amount, style = 1) {
    const formatted = new Intl.NumberFormat("vi-VN").format(amount);
    return style == 1 ? `VND ${formatted}` : `${formatted} ₫`;
}

export function url(path, lang_id) {
    path = path ?? "";
    path = path.replace(/^\/+/, "");

    if (path.length === 0) {
        return `/${lang_id}`;
    }
    return `/${lang_id}/${path.replace(/^\/+/, "")}`;
}

/**
 * Định dạng chuỗi thời gian thành ngày theo ngôn ngữ hiện tại.
 *
 * @param {string} time - Chuỗi thời gian theo định dạng "yyyy-MM-dd HH:mm:ss".
 * @returns {string} Chuỗi ngày đã được định dạng.
 *
 * - Nếu ngôn ngữ là "en", trả về dạng "MM-dd-yyyy" (ví dụ: "04-28-2025").
 * - Nếu ngôn ngữ khác (ví dụ "vi"), trả về dạng "dd-MM-yyyy" (ví dụ: "28-04-2025").
 */
export function renderDate(time, lang_id) {
    let time_new = 0;

    const parsedDate = parse(time, "yyyy-MM-dd HH:mm:ss", new Date());
    if (lang_id == "en") {
        time_new = format(parsedDate, "MM-dd-yyyy");
    } else {
        time_new = format(parsedDate, "dd-MM-yyyy");
    }

    return time_new;
}

import SeoSearchService from "@services/SeoSearchService";

/**
 * Utility function to add SEO data to route loader
 * @param {Function} originalLoader - The original loader function
 * @returns {Function} Enhanced loader with SEO data
 */
export const withSeoData = (originalLoader) => {
    return async (args) => {
        const { request } = args;
        const currentUrl = new URL(request.url).pathname;
        const lang_id = args?.params?.lang_id;
        // Get original data and SEO data in parallel
        const [originalData, seoData] = await Promise.all([
            originalLoader(args),
            SeoSearchService.getByUrl(currentUrl, lang_id),
        ]);

        return {
            ...originalData,
            seoData,
        };
    };
};

/**
 * Utility function to create meta with SEO fallback
 * @param {Function} getEntityData - Function to extract entity data from loader data
 * @param {string} defaultTitle - Default title if no SEO or entity title
 * @param {string} defaultDescription - Default description if no SEO or entity description
 * @returns {Function} Meta function with SEO support
 */
export const createSeoMeta = (
    getEntityData,
    defaultTitle = "Page",
    defaultDescription = "Page description"
) => {
    return ({ data }) => {
        const { seoData } = data || {};
        const seoInfo = seoData?.data;
        const entityData = getEntityData ? getEntityData(data) : null;
        return [
            {
                title: seoInfo?.title || entityData?.title || defaultTitle,
            },
            {
                name: "description",
                content:
                    seoInfo?.description ||
                    entityData?.description ||
                    entityData?.title ||
                    defaultDescription,
            },
            ...(seoInfo?.image
                ? [
                      {
                          name: "image",
                          content: seoInfo.image,
                      },
                  ]
                : entityData?.image
                ? [
                      {
                          name: "image",
                          content: entityData.image,
                      },
                  ]
                : []),
            {
                name: "keywords",
                content: seoInfo
                    ? seoInfo?.title +
                      "," +
                      seoInfo?.description +
                      "," +
                      formatStringWithCommas(seoInfo?.description)
                    : entityData?.title || defaultTitle,
            },
            {
                name: "robots",
                content: seoInfo
                    ? `${seoInfo?.robots_index == 1 ? "index" : "noindex"}, ${
                          seoInfo?.robots_follow == 1 ? "follow" : "nofollow"
                      }`
                    : "index, follow",
            },
        ];
    };
};

/**
 * Simplified meta creator for common entity patterns
 */
export const createEntityMeta = (
    entityKey,
    defaultTitle,
    defaultDescription
) => {
    return createSeoMeta(
        (data) => data?.[entityKey],
        defaultTitle,
        defaultDescription
    );
};

export const isProductionEnvironment = () => {
    return import.meta.env.VITE_IS_PRODUCTION;
};

/**
 * Định dạng chuỗi bằng cách thay thế tất cả khoảng trắng bên trong bằng dấu phẩy
 * Giữ nguyên khoảng trắng ở hai đầu chuỗi
 *
 * @param {string} str - Chuỗi cần định dạng
 * @returns {string} Chuỗi đã được định dạng với khoảng trắng bên trong thay bằng dấu phẩy
 */
export const formatStringWithCommas = (str) => {
    if (!str) return "";

    // Tìm khoảng trắng ở đầu và cuối chuỗi
    const leadingSpaces = str.match(/^\s*/)[0];
    const trailingSpaces = str.match(/\s*$/)[0];

    // Cắt chuỗi để loại bỏ khoảng trắng ở đầu và cuối
    const trimmedStr = str.trim();

    // Thay thế khoảng trắng bên trong bằng dấu phẩy
    let formattedStr = trimmedStr.replace(/\s+/g, ",");

    // Thay thế nhiều dấu phẩy cạnh nhau thành một dấu phẩy
    formattedStr = formattedStr.replace(/,+/g, ",");

    // Trả về chuỗi với khoảng trắng ở đầu và cuối được giữ nguyên
    return leadingSpaces + formattedStr + trailingSpaces;
};

/**
 * Trích xuất videoId từ URL YouTube và trả về URL dạng nhúng (embed)
 * @param url - URL YouTube người dùng nhập vào
 * @returns string | null - Trả về đường dẫn embed hoặc null nếu không hợp lệ
 */
export const getYoutubeEmbedUrl = (url) => {
    // 1. Xóa khoảng trắng đầu/cuối nếu có
    const trimmedUrl = url.trim();

    // 2. Nếu người dùng không nhập gì, trả về null
    if (!trimmedUrl) return null;

    // 3. Dùng regex để bắt videoId từ các dạng URL YouTube phổ biến
    const match = trimmedUrl.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );

    // 4. match[1] là videoId nếu regex khớp
    const videoId = match?.[1];

    // 5. Nếu không tìm được videoId → trả null
    if (!videoId) return null;

    // 6. Trả về đường dẫn nhúng iframe của YouTube
    return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Hàm lấy URL ảnh thumbnail từ link YouTube
 * @param url - Đường dẫn video YouTube (có thể là watch, embed, shorts hoặc youtu.be)
 * @returns URL của ảnh thumbnail hoặc null nếu không hợp lệ
 */
export const getYoutubeThumbnail = (url) => {
    if (typeof url !== "string") return url;
    // Dùng biểu thức chính quy để trích xuất video ID từ nhiều dạng link YouTube khác nhau
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );

    // Nếu không tìm thấy ID, trả về null
    const videoId = match?.[1];
    if (!videoId) return url;

    // Trả về URL ảnh thumbnail dạng chất lượng cao (hqdefault)
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

// Rút gọn số lớn với K, M, B
export const formatNumber = (num) => {
    if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    }
    if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (num >= 1_000) {
        return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return num.toString();
};

// Trả về ngày đầu của ngày đã cho
export const atStartOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

// Định dạng ngày thành chuỗi YYYY/MM/DD
export const formatYMD = (d) =>
    d.toLocaleDateString("sv-SE").replace(/-/g, "/"); // YYYY/MM/DD

// Tạo map giá từ mảng { sale_date, price_after_tax }
export function makePriceMap(ticketSevenDays) {
    const map = new Map();
    (ticketSevenDays || []).forEach((i) =>
        map.set(i.sale_date, i.price_after_tax)
    );
    return map;
}
